/* eslint-disable no-nested-ternary */
/* eslint-disable no-return-assign */
/* eslint-disable react/no-array-index-key */
import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import Shimmer from 'react-shimmer-effect';
import moment from 'moment-timezone';
import UnifiedDayHeader from './UnifiedDayHeader';
import DraggableContainer from './DraggableContainer';
import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  setExpandedItems,
  setWeekViewExpandedDay,
  setCurrentlyDisplayedWeekdays,
  setSelectedNumberOfDaysToShowInWeek,
  setSelectedProgramDayNumber,
} from '../../ducks/calendarActions';

const WeekContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 100%;
  gap: ${(props) => (props.viewType === 'weekView' ? '8px' : '1px')};
  background-color: #EEEEEE;
`;

const ScrollIconBox = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  border: 1px solid #EEEEEE;
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
  
   &:hover {
    border-color: #bdbdbd;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    background: #F5F5F5;
    transform: scale(0.96);
  }

`;

const MarginRight = styled('div')`
  position: fixed;
  top: ${(props) => props.headerHeight + (props.programType === 'free-form-program' ? 0 : 32) + 49}px;
  bottom: 48px;
  right: 0;          
  width: 8px;
  z-index: 90;        
  background-color: #F5F5F5;
`;

const MarginLeft = styled('div')`
  position: fixed;
  top: ${(props) => props.headerHeight + (props.programType === 'free-form-program' ? 0 : 32) + 49}px;
  bottom: 48px;
  left: ${(props) => `${(props.isPrimaryAnimatedSideBarOpen ? 384 : 0) + 48}px`};       
  width: 8px;
  z-index: 90;       
  background-color: #F5F5F5;

  @media screen and (max-width: 1023px) {
    left: ${(props) => `${(props.isPrimaryAnimatedSideBarOpen ? 300 : 0) + 48}px`}; 
  }
  
`;

const DayWrapper = styled('div')`
  flex: ${(props) => (props.isExpanded && props.weekDatesOrDaysLength > 1 ? '0 0 800px' : '1')};
  min-width: 175px;
  display: flex;
  flex-direction: column;
`;

const DayColumn = styled('div')`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 0 0 4px 4px;
  flex: 1;
  padding-top: ${(props) => !props.isWeekDayItemsLoading && '8px'};
  padding-left: ${(props) => props.addPaddingLeftAndRight && '5px'};
  padding-right: ${(props) => props.addPaddingLeftAndRight && '5px'};
`;

const StatsBar = styled('div')`
  background: white;
  min-height: 40px;
  border-top: 1px solid #EEEEEE;
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const WeekView = ({ isWeekDayItemsLoading }) => {
  const dispatch = useDispatch();
  const weekViewContainerRef = useRef();
  const horizontalItemRefs = useRef([]);
  const shimmerCount = [1, 2, 3, 4, 5];
  const headerHeight = useSelector((state) => state.calendar.data.headerHeight);
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const groupByFilter = useSelector(
    (state) => state.calendar.data.groupByFilter,
  );

  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts,
  );
  const afterRecurringWorkouts = useSelector((state) => state.calendar.data.afterRecurringWorkouts);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const isAnalyticsDisabled = useSelector(
    (state) => state.calendar.ui.isAnalyticsDisabled,
  );
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const expandedItems = useSelector((state) => state.calendar.data.expandedItems);
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const programItems = useSelector((state) => state.calendar.data.nonDateBasedProgramItems);
  const expandedDay = useSelector((state) => state.calendar.data.weekViewExpandedDay);
  const selectedNumberOfDaysToShowInWeek = useSelector(
    (state) => state.calendar.data.selectedNumberOfDaysToShowInWeek,
  );
  const selectedDaysOfWeekToShowInCurrentWeek = useSelector(
    (state) => state.calendar.data.selectedDaysOfWeekToShowInCurrentWeek,
  );
  const animatedSidebarContainerWidth = useSelector(
    (state) => state.calendar.data.animatedSidebarContainerWidth,
  );
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const mostRecentlyAddedCalendarItem = useSelector(
    (state) => state.calendar.data.mostRecentlyAddedCalendarItem,
  );
  const weekViewHorizontallyExpandedDay = useSelector(
    (state) => state.calendar.data.weekViewExpandedDay,
  );

  const numberOfTimesSelectedDayOccursInMonth = () => {
    const formatDate = moment(selectedDate).format('YYYY-MM-DD');
    const start = moment(formatDate).startOf('month');
    const end = moment(formatDate).endOf('month');

    const countDays = (day) => (day.isAfter(end, 'day')
      ? 0
      : (day.format('dddd') === selectedWeekViewDayToggle ? 1 : 0) + countDays(day.clone().add(1, 'day')));

    return countDays(start);
  };

  useEffect(() => {
    if (selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setSelectedNumberOfDaysToShowInWeek(numberOfTimesSelectedDayOccursInMonth()));
    }
    if (selectedWeekViewDayToggle === 'Everyday') {
      dispatch(setSelectedNumberOfDaysToShowInWeek(7));
    }
  }, [selectedWeekViewDayToggle]);

  const weekDatesWhenDayToggleSelected = useMemo(() => {
    const daysToInclude = selectedDaysOfWeekToShowInCurrentWeek;
    const daysToShowMax = selectedNumberOfDaysToShowInWeek || 7;
    const includeEmptyDays = daysToInclude.includes('Empty Days');

    const selectedMoment = moment(selectedDate);
    const month = selectedMoment.month(); /** 0-indexed */
    const year = selectedMoment.year();

    const dates = [];
    const datesForReduxTracking = [];

    /** Start from the first day of the month */
    const current = moment().year(year).month(month).date(1);

    /** Loop through all days of the month */
    while (current.month() === month) {
      const dayName = current.format('dddd');
      const formattedDate = current.format('YYYY-MM-DD');

      const athleteAddedItems = calendarItems[formattedDate]?.athleteAddedItems;

      const isDateEmpty = (!calendarItems[formattedDate]?.exercises
        || calendarItems[formattedDate].exercises?.length === 0)
        && (!beforeRecurringWorkouts[formattedDate]?.length
        && !afterRecurringWorkouts[formattedDate]?.length && !athleteAddedItems?.length);

      if (
        /** see if dayName we are looping is === to the selected toggle */
        dayName === selectedWeekViewDayToggle
      && (!isDateEmpty || includeEmptyDays)
      ) {
        dates.push(current.clone());
        datesForReduxTracking.push({ date: formattedDate, isDateEmpty });

        if (dates?.length >= daysToShowMax) {
          break;
        }
      }

      current.add(1, 'day');
    }

    if (selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setCurrentlyDisplayedWeekdays(datesForReduxTracking));
    }

    return dates;
  }, [
    selectedDate,
    selectedNumberOfDaysToShowInWeek,
    selectedDaysOfWeekToShowInCurrentWeek,
    calendarItems,
    selectedWeekViewDayToggle,
  ]);

  const weekDates = useMemo(() => {
    const daysToInclude = selectedDaysOfWeekToShowInCurrentWeek;
    const daysToShowMax = selectedNumberOfDaysToShowInWeek || 7;
    const includeEmptyDays = daysToInclude.includes('Empty Days');

    const dates = [];
    const datesForReduxTracking = [];

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 7; i++) {
      const current = moment(selectedDate).clone().add(i, 'days');
      const dayName = current.format('dddd');
      const formattedDate = current.format('YYYY-MM-DD');

      const athleteAddedItems = calendarItems[formattedDate]?.athleteAddedItems;

      const isDateEmpty = (!calendarItems[formattedDate]?.exercises
        || calendarItems[formattedDate].exercises?.length === 0)
        && (!beforeRecurringWorkouts[formattedDate]?.length
        && !afterRecurringWorkouts[formattedDate]?.length && !athleteAddedItems?.length);

      /** Only add if day is toggled on AND (it's not empty OR we're allowing empty days) */
      if (
        daysToInclude.includes(dayName)
      && (!isDateEmpty || includeEmptyDays)
      ) {
        dates.push(current);
        datesForReduxTracking.push({ date: formattedDate, isDateEmpty });
      }

      if (dates?.length >= daysToShowMax) {
        break;
      }
    }

    if (selectedWeekViewDayToggle === 'Everyday') {
      dispatch(setCurrentlyDisplayedWeekdays(datesForReduxTracking));
    }
    return dates;
  }, [
    selectedDate,
    selectedNumberOfDaysToShowInWeek,
    selectedDaysOfWeekToShowInCurrentWeek,
    calendarItems,
  ]);

  const programWeekDays = useMemo(() => {
    const selectedProgramDay = programItems[selectedProgramDayNumber];

    /** If no selected day or empty programItems, return default week */
    if (!selectedProgramDay || !Object.keys(programItems)?.length) {
      return [0, 1, 2, 3, 4, 5, 6];
    }

    const selectedWeek = selectedProgramDay.weekNumber;

    /** Get all day numbers in the selected week */
    const dayNumbersInSelectedWeek = Object.entries(programItems)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, item]) => item.weekNumber === selectedWeek)
      .map(([dayNumber]) => Number(dayNumber))
      .sort((a, b) => a - b); // ensure consistent ordering

    /** If selectedNumberOfDaysToShowInWeek is defined, apply the limit */
    if (selectedNumberOfDaysToShowInWeek && selectedNumberOfDaysToShowInWeek > 0) {
      return dayNumbersInSelectedWeek.slice(0, selectedNumberOfDaysToShowInWeek);
    }

    /** No limit applied → return all */
    return dayNumbersInSelectedWeek;
  }, [
    programItems,
    selectedProgramDayNumber,
    selectedNumberOfDaysToShowInWeek,
  ]);

  /** when the groupByFilter is changed we always need to set selected dayNumber back to the
  * first day of the program
   */
  useEffect(() => {
    if (programType === 'free-form-program' && viewType === 'weekView') {
      const dayNumbers = Object.keys(programItems)
        .map(Number)
        .sort((a, b) => a - b);

      if (dayNumbers?.length) {
        const lowestDayNumber = dayNumbers[0];
        if (lowestDayNumber !== selectedProgramDayNumber) {
          dispatch(setSelectedProgramDayNumber(lowestDayNumber));
        }
      }
    }
  }, [groupByFilter]);

  const programDaysGroupedByDay = useMemo(() => {
    const selectedProgramDay = programItems[selectedProgramDayNumber];

    /** If no selected day or empty programItems, return default week */
    if (!selectedProgramDay || !Object.keys(programItems)?.length) {
      return [0, 1, 2, 3, 4, 5, 6];
    }

    const selectedDayInWeek = selectedProgramDay.dayInWeek;

    /** Get all day numbers in the selected week */
    const dayNumbersInSelectedWeek = Object.entries(programItems)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, item]) => item.dayInWeek === selectedDayInWeek)
      .map(([dayNumber]) => Number(dayNumber))
      .sort((a, b) => a - b); /** ensure consistent ordering */

    /** If selectedNumberOfDaysToShowInWeek is defined, apply the limit */
    if (selectedNumberOfDaysToShowInWeek && selectedNumberOfDaysToShowInWeek > 0) {
      return dayNumbersInSelectedWeek.slice(0, selectedNumberOfDaysToShowInWeek);
    }

    /** No limit applied → return all */
    return dayNumbersInSelectedWeek;
  }, [
    programItems,
    selectedProgramDayNumber,
    selectedNumberOfDaysToShowInWeek,
  ]);

  /** ref pattern keeps callback identity stable while reading current values */
  const toggleExpandStateRef = useRef({
    expandedDay, programType, programItems, calendarItems, expandedItems,
  });
  toggleExpandStateRef.current = {
    expandedDay, programType, programItems, calendarItems, expandedItems,
  };

  const handleToggleExpand = useCallback((dayKey) => {
    const {
      expandedDay: ed, programType: pt, programItems: pi, calendarItems: ci, expandedItems: ei,
    } = toggleExpandStateRef.current;
    const isCurrentlyExpanded = ed === dayKey;
    dispatch(setWeekViewExpandedDay(isCurrentlyExpanded ? null : dayKey));

    const allExercises = Object.values(pt === 'free-form-program' ? pi : ci).flatMap((day) => day.exercises || []);

    const newExpandedItems = {};

    allExercises.forEach((exercise) => {
      const { assignedId, type } = exercise;
      const isSessionBreak = type === 'session_break';
      if (!assignedId) return;

      /** Preserve whatever the user had — truthy keeps expanded, false keeps
       * user-collapsed (stored via SET_SESSION_BREAK_COLLAPSED). A bare truthy
       * check would drop the false entries, and the reducer's expansion helper
       * would then auto-expand them on the next setCalendarItems pass. */
      if (isSessionBreak && Object.prototype.hasOwnProperty.call(ei || {}, assignedId)) {
        newExpandedItems[assignedId] = ei[assignedId];
      }
    });

    dispatch(setExpandedItems(newExpandedItems));
  }, [dispatch]);

  const filteredWeekDates = selectedWeekViewDayToggle !== 'Everyday' ? weekDatesWhenDayToggleSelected : weekDates;
  const filteredProgramWeekDays = groupByFilter === 'Day' ? programDaysGroupedByDay : programWeekDays;

  const expandDayCallback = useCallback((dayKey) => {
    dispatch(setWeekViewExpandedDay(dayKey));
  }, [dispatch]);

  const weekDatesOrDays = programType === 'free-form-program' ? filteredProgramWeekDays : filteredWeekDates;

  useEffect(() => {
    if (!weekViewHorizontallyExpandedDay || !Array.isArray(weekDatesOrDays)) return;

    /** Find the matching index */
    const matchingIndex = programType === 'free-form-program' ? weekDatesOrDays.findIndex((dayNumberIndex) => dayNumberIndex === weekViewHorizontallyExpandedDay) : weekDatesOrDays.findIndex((date) => moment(date).format('YYYY-MM-DD') === weekViewHorizontallyExpandedDay);

    if (matchingIndex === -1) {
      return;
    }

    const dayColumn = horizontalItemRefs.current[matchingIndex];
    if (dayColumn) {
      /** Skip if sidebar is open AND width is < 976 */
      if (isPrimaryAnimatedSideBarOpen && window.innerWidth < 976) {
        return; /** skip the scrollIntoView */
      }
      const delay = mostRecentlyAddedCalendarItem ? 400 : 350;
      setTimeout(() => {
        dayColumn.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      }, delay);
    }
  }, [weekViewHorizontallyExpandedDay]);

  const leftScrollRef = useRef(null);
  const rightScrollRef = useRef(null);

  useEffect(() => {
    const checkOverflow = () => {
      const el = document.documentElement;
      const { scrollLeft, scrollWidth } = el;
      const clientWidth = window.innerWidth;

      if (leftScrollRef.current) {
        leftScrollRef.current.style.visibility = scrollLeft > 0 ? 'visible' : 'hidden';
      }

      if (rightScrollRef.current) {
        rightScrollRef.current.style.visibility = scrollLeft + clientWidth < scrollWidth ? 'visible' : 'hidden';
      }
    };

    checkOverflow(); /** run immediately */
    const timeoutId = setTimeout(checkOverflow, 350); /** after sidebar animation */

    window.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [isPrimaryAnimatedSideBarOpen, weekViewHorizontallyExpandedDay]);

  const leftRafId = useRef(null);
  const rightRafId = useRef(null);

  const scrollLeftStep = () => {
    const el = document.documentElement;
    if (el.scrollLeft > 0) {
      /** only scroll left if not already at leftmost */
      window.scrollBy({ left: -5, behavior: 'auto' });
      leftRafId.current = requestAnimationFrame(scrollLeftStep);
    } else {
      leftRafId.current = null; /** stop scrolling if reached left */
    }
  };

  const scrollRightStep = () => {
    const el = document.documentElement;
    if (el.scrollLeft + window.innerWidth < el.scrollWidth) {
      /** only scroll right if not already at rightmost */
      window.scrollBy({ left: 5, behavior: 'auto' });
      rightRafId.current = requestAnimationFrame(scrollRightStep);
    } else {
      rightRafId.current = null; /** stop scrolling if reached right */
    }
  };

  const handleLeftMouseEnter = () => {
    if (!leftRafId.current) leftRafId.current = requestAnimationFrame(scrollLeftStep);
  };

  const handleLeftMouseLeave = () => {
    if (leftRafId.current) {
      cancelAnimationFrame(leftRafId.current);
      leftRafId.current = null;
    }
  };

  const handleRightMouseEnter = () => {
    if (!rightRafId.current) rightRafId.current = requestAnimationFrame(scrollRightStep);
  };

  const handleRightMouseLeave = () => {
    if (rightRafId.current) {
      cancelAnimationFrame(rightRafId.current);
      rightRafId.current = null;
    }
  };

  return (
    <>
      <ScrollIconBox
        ref={leftScrollRef}
        style={{
          position: 'fixed',
          top: '50%',
          left: (isPrimaryAnimatedSideBarOpen ? (window.innerWidth < 1023 ? 360 : 444) : 60),
          zIndex: 1000,
          visibility: 'hidden',
        }}
        onMouseDown={handleLeftMouseEnter}
        onMouseUp={handleLeftMouseLeave}
        onMouseLeave={handleLeftMouseLeave}
      >
        <IcomoonReact
          iconSet={iconSet}
          size={14}
          icon='left-arrow'
          color='#9E9E9E'
        />
      </ScrollIconBox>

      <WeekContainer
        ref={weekViewContainerRef}
        viewType={viewType}
      >
        {weekDatesOrDays?.map((dateOrDay, index) => {
          const dayKey = programType === 'free-form-program' ? dateOrDay : dateOrDay.format('YYYY-MM-DD');
          const isExpanded = expandedDay === dayKey;

          return (
            <DayWrapper
              weekDatesOrDaysLength={weekDatesOrDays?.length}
              key={`day-${dayKey}`}
              isExpanded={isExpanded}
              ref={(el) => (horizontalItemRefs.current[index] = el)}
            >
              <UnifiedDayHeader
                programType={programType}
                viewType='weekView'
                date={dateOrDay}
                isExpanded={isExpanded}
                onToggleExpand={handleToggleExpand}
                dayKey={dayKey}
                showWorkoutName
                isWeekView
                dateOrDay={dateOrDay}
                filteredWeekDates={filteredWeekDates}
                filteredProgramWeekDays={filteredProgramWeekDays}
              />
              <DayColumn
                isWeekDayItemsLoading={isWeekDayItemsLoading}
                addPaddingLeftAndRight={((String(weekViewHorizontallyExpandedDay)
                === String(dayKey)))}
              >
                {isWeekDayItemsLoading ? (
                  shimmerCount.map((_, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex', paddingLeft: 8, paddingRight: 8, paddingTop: i === 0 ? 8 : 4, paddingBottom: 4,
                      }}
                    >
                      <Shimmer>
                        <div
                          style={{
                            height: 64,
                            backgroundColor: '#f5f5f5',
                            width: '100%',
                          }}
                        />
                      </Shimmer>
                    </div>
                  ))
                ) : (
                  <>
                    <DraggableContainer
                      id={`week-day-${dayKey}`}
                      date={dateOrDay}
                      dayKey={dayKey}
                      isWeekView
                      isDayExpanded={isExpanded}
                      onDayExpand={handleToggleExpand}
                      expandDay={expandDayCallback}
                    />
                    {((Object.keys(selectedReduxCalendar)?.length
                || Object.keys(selectedReduxAthlete)?.length
                || Object.keys(selectedReduxNonDateBasedProgram)?.length))
                && !isAnalyticsDisabled ? (
                  <StatsBar>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      gap: '16px',
                    }}
                    >
                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      >
                        <Text fontSize='13px' color='#BDBDBD'>Sets</Text>
                        <Text
                          marginLeft='5px'
                          marginTop='2px'
                          fontSize='15px'
                          color='#616161'
                          fontWeight='500'
                        >
                          {programType === 'free-form-program'
                            ? (programItems[dateOrDay]?.setCount || 0)
                            : (calendarItems[dayKey]?.setCount || 0)}
                        </Text>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      >
                        <Text fontSize='13px' color='#BDBDBD'>Reps</Text>
                        <Text
                          marginLeft='5px'
                          marginTop='2px'
                          fontSize='15px'
                          color='#616161'
                          fontWeight='500'
                        >
                          {programType === 'free-form-program'
                            ? (programItems[dateOrDay]?.repCount || 0)
                            : (calendarItems[dayKey]?.repCount || 0)}
                        </Text>
                      </div>
                    </div>
                  </StatsBar>
                      ) : null}
                  </>
                )}
              </DayColumn>
            </DayWrapper>
          );
        })}
        {/* we need to add fixed position margins because
        nested scroll only work if we allow the browser to control scroll
        this mean we lose our margins that we would get from
        a nested scroll container so we are just creating them for style  */}
        <MarginLeft
          headerHeight={headerHeight}
          animatedSidebarContainerWidth={animatedSidebarContainerWidth}
          isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
          programType={programType}
        />
        <MarginRight headerHeight={headerHeight} programType={programType} />
      </WeekContainer>
      <ScrollIconBox
        ref={rightScrollRef}
        style={{
          position: 'fixed',
          top: '50%',
          right: 12,
          zIndex: 1000,
          visibility: 'hidden',
        }}
        onMouseDown={handleRightMouseEnter}
        onMouseUp={handleRightMouseLeave}
        onMouseLeave={handleRightMouseLeave}
      >
        <IcomoonReact
          iconSet={iconSet}
          size={14}
          icon='right-arrow'
          color='#9E9E9E'
        />
      </ScrollIconBox>
    </>
  );
};

WeekView.propTypes = {
  isWeekDayItemsLoading: PropTypes.bool,
};

WeekView.defaultProps = {
  isWeekDayItemsLoading: false,

};

export default WeekView;
